import React, { memo, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { Paper, Typography, Box, alpha, Chip } from '@mui/material';
import { Storage } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const DatabaseNode = memo(({ data, selected, id }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Node color - Use theme secondary colors
  const nodeColor = theme.palette.secondary.main;
  const textColor = theme.palette.secondary.contrastText;
  const borderColor = selected ? theme.palette.secondary.main : 'transparent';

  const handleDoubleClick = useCallback((event) => {
    event.stopPropagation();
    const editEvent = new CustomEvent('editNode', { detail: { nodeId: id } });
    window.dispatchEvent(editEvent);
  }, [id]);

  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const contextEvent = new CustomEvent('nodeContextMenu', {
      detail: {
        nodeId: id,
        x: event.clientX,
        y: event.clientY
      }
    });
    window.dispatchEvent(contextEvent);
  }, [id]);

  const getOperationColor = (operation) => {
    switch(operation?.toUpperCase()) {
      case 'CREATE': return theme.palette.success.main;
      case 'READ': return theme.palette.info.main;
      case 'UPDATE': return theme.palette.warning.main;
      case 'DELETE': return theme.palette.error.main;
      case 'FIND': return theme.palette.info.dark;
      case 'AGGREGATE': return theme.palette.text.secondary;
      default: return theme.palette.text.disabled;
    }
  };

  return (
    <Paper
      elevation={selected ? 12 : 4}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      sx={{
        padding: 2.5,
        borderRadius: 3,
        minWidth: 200,
        minHeight: 100,
        position: 'relative',
        overflow: 'visible',
        cursor: 'move',
        background: isDark
          ? `linear-gradient(145deg, ${alpha('#0D2E4D', 0.9)}, ${alpha('#0D2E4D', 0.8)})`
          : `linear-gradient(145deg, ${alpha(nodeColor, 0.95)}, ${alpha(nodeColor, 0.85)})`,
        backdropFilter: 'blur(10px)',
        border: selected
          ? `2px solid ${theme.palette.secondary.main}`
          : `1px solid ${alpha(nodeColor, 0.3)}`,
        transition: theme.transitions.create([
          'transform', 'box-shadow', 'border'
        ], {
          duration: theme.transitions.duration.shorter,
        }),
        '&:hover': {
          transform: 'translateY(-4px) scale(1.02)',
          boxShadow: theme.shadows[16],
          border: `1px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
          '& .storage-icon': {
            transform: 'scale(1.1)',
          }
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: 'inherit',
          background: `radial-gradient(circle at top right, ${alpha('#f2c526', 0.1)}, transparent)`,
          pointerEvents: 'none',
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Database node: ${data.label || 'Database Operation'}`}
      aria-selected={selected}
    >
      {/* Handles */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: `linear-gradient(135deg, ${nodeColor} 0%, #7b1fa2 100%)`,
          border: `2px solid ${theme.palette.background.paper}`,
          width: 14,
          height: 14,
          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          p: 1.5,
          mx: -1,
          mt: -1,
          borderRadius: 2,
          background: alpha(nodeColor, 0.15),
          backdropFilter: 'blur(8px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            p: 0.5,
            borderRadius: 1.5,
            background: `linear-gradient(135deg, ${nodeColor} 0%, #7b1fa2 100%)`,
            display: 'flex',
            color: 'white',
            boxShadow: `0 2px 8px ${alpha(nodeColor, 0.3)}`,
          }}>
            <Storage className="storage-icon" sx={{ fontSize: 18, transition: 'transform 0.3s ease' }} />
          </Box>
          <Typography variant="subtitle1" fontWeight="600" color={textColor}>
            {data.label || 'Database Operation'}
          </Typography>
        </Box>
      </Box>

      {/* Description */}
      {data.description && (
        <Typography
          variant="body2"
          sx={{
            mb: 2,
            color: alpha(textColor, 0.85),
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
          }}
        >
          {data.description}
        </Typography>
      )}

      {/* Database Fields */}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
        {data.operation && (
          <Chip
            label={data.operation.toUpperCase()}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 600,
              backgroundColor: alpha(getOperationColor(data.operation), 0.2),
              color: getOperationColor(data.operation),
              border: `1px solid ${alpha(getOperationColor(data.operation), 0.4)}`,
            }}
          />
        )}
        {data.collection && (
          <Chip
            label={data.collection.length > 15 ? `${data.collection.substring(0, 15)}...` : data.collection}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              backgroundColor: alpha(theme.palette.warning.light, 0.2),
              color: theme.palette.warning.dark,
              border: `1px solid ${alpha(theme.palette.warning.light, 0.4)}`,
            }}
          />
        )}
        {data.query && (
          <Chip
            label="Query"
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              backgroundColor: alpha(theme.palette.info.light, 0.2),
              color: theme.palette.info.dark,
              border: `1px solid ${alpha(theme.palette.info.light, 0.4)}`,
            }}
          />
        )}
      </Box>

      {/* Query Preview if available */}
      {data.query && (
        <Box
          sx={{
            mt: 1,
            p: 1,
            backgroundColor: alpha(textColor, 0.1),
            borderRadius: 1,
            border: `1px solid ${alpha(textColor, 0.2)}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: alpha(textColor, 0.8),
              fontSize: '0.7rem',
              fontFamily: 'monospace',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '160px',
            }}
          >
            {typeof data.query === 'object'
              ? JSON.stringify(data.query).substring(0, 25) + '...'
              : data.query.substring(0, 25) + (data.query.length > 25 ? '...' : '')
            }
          </Typography>
        </Box>
      )}

      {/* Status Indicator */}
      <Box
        sx={{
          position: 'absolute',
          top: -8,
          right: -8,
          width: 24,
          height: 24,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${nodeColor} 0%, #7b1fa2 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.2)}`,
          border: `2px solid ${theme.palette.background.paper}`,
        }}
      >
        <Storage sx={{ fontSize: 14 }} />
      </Box>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: `linear-gradient(135deg, ${nodeColor} 0%, #7b1fa2 100%)`,
          border: `2px solid ${theme.palette.background.paper}`,
          width: 14,
          height: 14,
          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
        }}
      />
    </Paper>
  );
});

DatabaseNode.displayName = 'DatabaseNode';

export default DatabaseNode;